import { useCallback, useEffect, useMemo, useState } from "react";
import { Flex, Text, Badge, Button } from "@radix-ui/themes";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { Pencil1Icon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { RowActions, SimpleTable } from "@/v2/components/table/SimpleTable";
import {
  ArticleResource,
  ReleaseAccessStatus,
  ReleaseAccessResourceTopic,
  getArticleSource,
  articleSourceLabel,
  articleSourceColor,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.model";
import {
  useGetArticlesByRelease,
  useUpdateArticleAccess,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.api";
import { useGetTopics } from "@/api/useGetTopics";
import { AccessBadge } from "../components/AccessBadge";
import { ManageAccessDialog, Topic } from "../components/ManageAccessDialog";
import {
  ResourcesToolbar,
  ResourcesFilterState,
} from "../components/ResourcesToolbar";
import { File02Icon } from "@/v2/assets/icons/File02Icon";
import { useBrands } from "@/api/useBrands";

interface ArticlesTabProps {
  releaseId: number | undefined;
  publishedReleaseId?: number;
  simulatorId?: string;
}

type ArticleRow = ArticleResource & {
  isSelected: boolean;
  isStaged: boolean;
  publishedTopicsAccess: ReleaseAccessResourceTopic[];
};

export function ArticlesTab({ releaseId, publishedReleaseId, simulatorId }: ArticlesTabProps) {
  const { data: articles = [], isLoading, isError } = useGetArticlesByRelease(releaseId);
  const { data: publishedArticles = [] } = useGetArticlesByRelease(publishedReleaseId);
  const { data: topicsData } = useGetTopics();
  const { data: brandsData } = useBrands();
  const updateArticleAccess = useUpdateArticleAccess(releaseId, simulatorId);

  const topics: Topic[] = useMemo(() => {
    if (!topicsData) return [];
    return topicsData.map((t: { id: number; topic: string }) => ({
      id: t.id,
      topic: t.topic,
    }));
  }, [topicsData]);

  const categories = useMemo(() => {
    const uniqueCategories = new Set(
      articles
        .map((article) => article.category)
        .filter((category): category is string => Boolean(category?.trim()))
    );

    return ["No category", ...Array.from(uniqueCategories).sort((a, b) => a.localeCompare(b))];
  }, [articles]);

  const filterByBrands = useMemo(() => {
    return articles.some(a => a.brand && a.brand !== "null")
  }, [articles, brandsData]);
  
  const brands = useMemo(() => {
    if (brandsData?.length && brandsData.length > 0) {
      return ["No brand", ...brandsData.map((brand) => brand.name).sort((a, b) => a.localeCompare(b))];
    }
    return [];
  }, [brandsData]);

  const brandMap = useMemo(() => {
    return new Map((brandsData ?? []).map((brand) => [brand.id.toString(), brand.name]));
  }, [brandsData]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterState, setFilterState] = useState<ResourcesFilterState>({
    accessLevels: [],
    searchQuery: "",
    categories: [],
  });
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    resourceIds: string[];
    resourceName: string;
    currentStatus?: ReleaseAccessStatus;
    currentTopicsAccess?: ReleaseAccessResourceTopic[];
    publishedTopicsAccess?: ReleaseAccessResourceTopic[];
    currentDefaultTopicAccess?: ReleaseAccessStatus | null;
  }>({
    open: false,
    resourceIds: [],
    resourceName: "",
  });

  const publishedStatusMap = useMemo(() => {
    const map = new Map<string, ReleaseAccessStatus>();
    for (const article of publishedArticles) {
      map.set(article.id, article.accessStatus);
    }
    return map;
  }, [publishedArticles]);

  const publishedTopicsAccessMap = useMemo(() => {
    const map = new Map<string, ReleaseAccessResourceTopic[]>();
    for (const article of publishedArticles) {
      map.set(article.id, article.topicsAccess);
    }
    return map;
  }, [publishedArticles]);

  const filteredArticles = useMemo(() => {
    return articles.filter((article) => {
      const matchesSearch =
        filterState.searchQuery === "" ||
        article.title.toLowerCase().includes(filterState.searchQuery.toLowerCase());

      const matchesAccess =
        filterState.accessLevels.length === 0 ||
        filterState.accessLevels.includes(article.accessStatus);

      // If filtering by brands, check if the brand is in the filter state
      // If filtering by categories, check if the category is in the filter state
      const matchesCategory =
        (!filterByBrands && 
          (filterState.categories.length === 0 ||
            filterState.categories.includes(article.category || "No category"))
        ) || 
        (filterByBrands &&
          (filterState.categories.length === 0 ||
          filterState.categories.includes(brandMap.get(article.brand ?? "") ?? "No brand")));

      return matchesSearch && matchesAccess && matchesCategory;
    });
  }, [articles, filterState, filterByBrands, brandMap]);

  const rows: ArticleRow[] = useMemo(() => {
    return filteredArticles.map((article) => {
      const publishedStatus = publishedStatusMap.get(article.id);
      const isStaged =
        publishedReleaseId !== undefined &&
        publishedStatus !== undefined &&
        publishedStatus !== article.accessStatus;

      return {
        ...article,
        isSelected: selectedIds.has(article.id),
        isStaged,
        publishedTopicsAccess: publishedTopicsAccessMap.get(article.id) ?? [],
      };
    }).sort((a, b) => a.title.localeCompare(b.title) ?? 0);
  }, [filteredArticles, selectedIds, publishedStatusMap, publishedTopicsAccessMap, publishedReleaseId]);

  const PAGE_SIZE = 100;
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = useMemo(() => Math.ceil(rows.length / PAGE_SIZE), [rows.length]);

  const paginatedRows = useMemo(() => {
    const start = currentPage * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, currentPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [filterState]);

  const allSelected = useMemo(() => {
    return paginatedRows.length > 0 && paginatedRows.every((row) => row.isSelected);
  }, [paginatedRows]);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedRows.map((r) => r.id)));
    }
  }, [allSelected, paginatedRows]);

  const handleSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleOpenManageAccess = useCallback(
    (
      resourceIds: string[],
      resourceName: string,
      currentStatus?: ReleaseAccessStatus,
      currentTopicsAccess?: ReleaseAccessResourceTopic[],
      publishedTopicsAccess?: ReleaseAccessResourceTopic[],
      currentDefaultTopicAccess?: ReleaseAccessStatus | null
    ) => {
      setDialogState({
        open: true,
        resourceIds,
        resourceName,
        currentStatus,
        currentTopicsAccess,
        publishedTopicsAccess,
        currentDefaultTopicAccess,
      });
    },
    []
  );

  const handleUpdateAccess = useCallback(
    async (
      status: ReleaseAccessStatus,
      topicsAccess?: ReleaseAccessResourceTopic[],
      defaultTopicAccess?: ReleaseAccessStatus | null
    ) => {
      await updateArticleAccess.mutateAsync({
        articles: dialogState.resourceIds.map((id) => ({
          id,
          status,
          topicsAccess,
          defaultTopicAccess,
        })),
      });
      setDialogState((prev) => ({ ...prev, open: false }));
      setSelectedIds(new Set());
    },
    [dialogState.resourceIds, updateArticleAccess]
  );

  const handleBulkAccessClick = useCallback(() => {
    const selectedArticles = rows.filter((r) => selectedIds.has(r.id));
    if (selectedArticles.length === 0) return;

    handleOpenManageAccess(
      Array.from(selectedIds),
      `${selectedArticles.length} articles`,
      selectedArticles[0].accessStatus,
      selectedArticles[0].topicsAccess,
      selectedArticles[0].publishedTopicsAccess,
      selectedArticles[0].defaultTopicAccess
    );
  }, [rows, selectedIds, handleOpenManageAccess]);

  const getRowActions = useCallback(
    (row: ArticleRow): RowActions<ArticleRow> => [
      {
        label: "Manage Access",
        icon: <Pencil1Icon />,
        onClick: () =>
          handleOpenManageAccess([row.id], row.title, row.accessStatus, row.topicsAccess, row.publishedTopicsAccess, row.defaultTopicAccess),
      },
    ],
    [handleOpenManageAccess]
  );

  const handleRowClick = useCallback(
    (row: ArticleRow) => {
      handleOpenManageAccess([row.id], row.title, row.accessStatus, row.topicsAccess, row.publishedTopicsAccess, row.defaultTopicAccess);
    },
    [handleOpenManageAccess]
  );

  const columns: ColumnDef<ArticleRow>[] = useMemo(
    () => [
      {
        id: "select",
        header: () => (
          <Checkbox
            checked={allSelected}
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.original.isSelected}
            onCheckedChange={() => handleSelectRow(row.original.id)}
            aria-label={`Select ${row.original.title}`}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        meta: {
          className: "w-[40px]",
        },
      },
      {
        header: "Article Name",
        accessorKey: "title",
        cell: ({ row }) => (
          <Flex align="center" gap="2">
            <Flex
              align="center"
              justify="center"
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: "var(--blue-3)",
                borderRadius: "4px",
                flexShrink: 0,
              }}
            >
              <File02Icon fill="sky" size={12} />
            </Flex>
            <Text size="2" color="gray" weight="medium" highContrast truncate>
              {row.original.title}
            </Text>
          </Flex>
        ),
      },
      {
        id: "source",
        header: "Source",
        cell: ({ row }) => {
          const source = getArticleSource(row.original.url);
          return (
            <Badge color={articleSourceColor[source] as any} size="1" variant="soft">
              {articleSourceLabel[source]}
            </Badge>
          );
        },
        meta: {
          className: "w-[120px]",
          cellTooltip: false,
        },
      },
      {
        header: filterByBrands ? "Brand" : "Category",
        accessorKey: filterByBrands ? "brand" : "category",
        cell: ({ row }) => (
          <Badge color="gray" size="1" variant="soft">
            {filterByBrands ? brandMap.get(row.original.brand ?? "") ?? "No brand" : row.original.category ?? "Uncategorized"}
          </Badge>
        ),
        meta: {
          className: "w-[200px]",
        },
      },
      {
        header: "Access",
        accessorKey: "accessStatus",
        cell: ({ row }) => (
          <AccessBadge
            status={row.original.accessStatus}
            isStaged={row.original.isStaged}
            topicsAccess={row.original.topicsAccess}
            publishedTopicsAccess={row.original.publishedTopicsAccess}
            topics={topics}
          />
        ),
        meta: {
          className: "w-[180px]",
          cellTooltip: false,
        },
      },
    ],
    [allSelected, handleSelectAll, handleSelectRow, topics]
  );

  return (
    <Flex direction="column" width="100%">
      <ResourcesToolbar
        title="Articles"
        count={filteredArticles.length}
        filterState={filterState}
        onFilterChange={setFilterState}
        selectedCount={selectedIds.size}
        onBulkAccessClick={handleBulkAccessClick}
        categoryFilter={{
          title: filterByBrands ? "Brand" : "Category",
          options: filterByBrands ? brands : categories,
        }}
      />

      {isError ? (
        <Flex align="center" justify="center" py="9">
          <Text size="2" color="red">
            Failed to load articles. Please try again.
          </Text>
        </Flex>
      ) : rows.length === 0 && !isLoading && (filterState.searchQuery || filterState.accessLevels.length > 0) ? (
        <Flex align="center" justify="center" py="9">
          <Text size="2" color="gray">
            No results found
          </Text>
        </Flex>
      ) : (
        <>
          <SimpleTable
            data={paginatedRows}
            columns={columns}
            loading={isLoading}
            getRowActions={getRowActions}
            onRowClicked={handleRowClick}
          />
          {totalPages > 1 && (
            <Flex align="center" justify="center" gap="3" py="3">
              <Button
                size="1"
                variant="soft"
                color="gray"
                disabled={currentPage === 0}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <ChevronLeftIcon />
                Previous
              </Button>
              <Text size="2" color="gray">
                {currentPage + 1} of {totalPages}
              </Text>
              <Button
                size="1"
                variant="soft"
                color="gray"
                disabled={currentPage >= totalPages - 1}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
                <ChevronRightIcon />
              </Button>
            </Flex>
          )}
        </>
      )}

      <ManageAccessDialog
        open={dialogState.open}
        onOpenChange={(open) => setDialogState((prev) => ({ ...prev, open }))}
        resourceName={dialogState.resourceName}
        resourceCount={dialogState.resourceIds.length > 1 ? dialogState.resourceIds.length : undefined}
        currentStatus={dialogState.currentStatus}
        currentTopicsAccess={dialogState.currentTopicsAccess}
        publishedTopicsAccess={dialogState.publishedTopicsAccess}
        currentDefaultTopicAccess={dialogState.currentDefaultTopicAccess}
        topics={topics}
        onUpdateAccess={handleUpdateAccess}
        isLoading={updateArticleAccess.isLoading}
      />
    </Flex>
  );
}
